"use client";

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from "react";
import type {
  ScheduleBookingConfig,
  MemberBookingConfig,
  BookingAppointmentForm,
  BookingOpeningsClient,
  ServiceRequestPayload,
} from "@openings/template-components";
import {
  bookingFlowApi,
  bookingOpeningsClient,
} from "@/lib/api/bookingFlowApi";
import { captureBookingUtm } from "@/lib/bookingSource";
import { useApp } from "@/lib/hooks/useAppointment";
import type { AppointmentForm } from "@/lib/hooks/useAppointment";
import AppointmentFlowContent from "./AppointmentModal/AppointmentFlowContent";
import type { BookingData } from "./bookingData";

// ---------------------------------------------------------------------------
// Context value
// ---------------------------------------------------------------------------

type BookingContextValue = {
  booking: ScheduleBookingConfig | MemberBookingConfig | undefined;
};

const BookingContext = createContext<BookingContextValue | null>(null);

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useBookingContext(): BookingContextValue {
  const ctx = useContext(BookingContext);
  if (!ctx) {
    throw new Error("useBookingContext must be used within a BookingProvider");
  }
  return ctx;
}

// ---------------------------------------------------------------------------
// useBooking — builds the full booking config from serializable data
// ---------------------------------------------------------------------------

export function useBooking(
  bookingData: BookingData | undefined,
  memberPageBaseHref?: string,
): ScheduleBookingConfig | MemberBookingConfig | undefined {
  const { openAppointmentForm } = useApp();

  const completionMode =
    bookingData?.type === "schedule"
      ? (bookingData.completionMode ?? "inline")
      : bookingData?.type === "member"
        ? // Member bookings only override when explicitly set; the standalone
          // member page passes "inline" (the template team pages that relied
          // on the redirect default were removed in Oct 2026).
          bookingData.completionMode
        : undefined;

  const openingsClient = useMemo<BookingOpeningsClient>(
    () => bookingOpeningsClient,
    [],
  );

  const handleOpenAppointmentForm = useCallback(
    (form: BookingAppointmentForm) => {
      const webForm: AppointmentForm = { ...form, completionMode };
      openAppointmentForm(webForm);
    },
    [openAppointmentForm, completionMode],
  );

  const submitServiceRequest = useCallback(
    async (payload: ServiceRequestPayload) => {
      const teamMemberId =
        bookingData?.type === "member" ? bookingData.teamMemberId : undefined;
      await bookingFlowApi.createServiceRequest(
        teamMemberId
          ? {
              ...payload,
              userId: undefined,
              teamMemberId: teamMemberId ?? payload.teamMemberId,
            }
          : payload,
      );
    },
    [bookingData],
  );

  const uploadMedia = useCallback(
    async (input: {
      filename: string;
      filetype: string;
      base64String: string;
    }) => {
      const res = await bookingFlowApi.uploadMediaPublic(input);
      return { data: { url: (res.data as any).url } };
    },
    [],
  );

  const renderInlineAppointment = useCallback(
    (form: BookingAppointmentForm, onClose: () => void) => {
      const webForm: AppointmentForm = { ...form, completionMode };
      return (
        <AppointmentFlowContent
          appointmentForm={webForm}
          onClose={onClose}
          completionMode={completionMode}
          showFooter={false}
        />
      );
    },
    [completionMode],
  );

  return useMemo<
    ScheduleBookingConfig | MemberBookingConfig | undefined
  >(() => {
    if (!bookingData) return undefined;

    if (bookingData.type === "schedule") {
      return {
        type: "schedule" as const,
        salonId: bookingData.salonId,
        services: bookingData.services,
        schedules: bookingData.schedules,
        cancellationPolicyEnabled: bookingData.cancellationPolicyEnabled,
        cancellationPolicyHours: bookingData.cancellationPolicyHours,
        cancellationPolicyNote: bookingData.cancellationPolicyNote,
        lateGraceMinutes: bookingData.lateGraceMinutes,
        bookingHorizonDays: bookingData.bookingHorizonDays,
        bookingTimeZone: bookingData.bookingTimeZone,
        completionMode: bookingData.completionMode,
        hasTeamMembers: bookingData.hasTeamMembers,
        showSchedule: bookingData.showSchedule,
        showLocationSummary: bookingData.showLocationSummary,
        showAvailabilityStatus: bookingData.showAvailabilityStatus,
        verificationMethod: bookingData.verificationMethod,
        initialScheduleId: bookingData.initialScheduleId,
        memberPageBaseHref,
        openingsClient,
        openAppointmentForm: handleOpenAppointmentForm,
        submitServiceRequest,
        uploadMedia,
        renderInlineAppointment,
      };
    }

    return {
      type: "member" as const,
      salonId: bookingData.salonId,
      user: bookingData.user,
      teamMemberId: bookingData.teamMemberId,
      services: bookingData.services,
      schedules: bookingData.schedules,
      vacations: bookingData.vacations,
      dateLabel: bookingData.dateLabel,
      serviceLabel: bookingData.serviceLabel,
      showLocationSummary: bookingData.showLocationSummary,
      showAvailabilityStatus: bookingData.showAvailabilityStatus,
      cancellationPolicyEnabled: bookingData.cancellationPolicyEnabled,
      cancellationPolicyHours: bookingData.cancellationPolicyHours,
      cancellationPolicyNote: bookingData.cancellationPolicyNote,
      lateGraceMinutes: bookingData.lateGraceMinutes,
      verificationMethod: bookingData.verificationMethod,
      bookingHorizonDays: bookingData.bookingHorizonDays,
      bookingTimeZone: bookingData.bookingTimeZone,
      openingsClient: bookingOpeningsClient,
      openAppointmentForm: handleOpenAppointmentForm,
      submitServiceRequest,
      uploadMedia,
      renderInlineAppointment,
    };
  }, [
    bookingData,
    memberPageBaseHref,
    openingsClient,
    handleOpenAppointmentForm,
    submitServiceRequest,
    uploadMedia,
    renderInlineAppointment,
  ]);
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

type BookingProviderProps = {
  bookingData?: BookingData;
  memberPageBaseHref?: string;
  children: React.ReactNode;
};

export function BookingProvider({
  bookingData,
  memberPageBaseHref,
  children,
}: BookingProviderProps) {
  const booking = useBooking(bookingData, memberPageBaseHref);
  // Capture UTM attribution as early as possible so it survives navigation
  // before the booking is completed (persisted in sessionStorage).
  useEffect(() => {
    captureBookingUtm();
  }, []);
  const value = useMemo(() => ({ booking }), [booking]);
  return (
    <BookingContext.Provider value={value}>{children}</BookingContext.Provider>
  );
}
